import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Event } from "@/types";
import { EventCard } from "./EventCard";

interface EventListProps {
  events: Event[];
  view: "upcoming" | "past";
  isLoading?: boolean;
  onView?: (event: Event) => void;
  emptyMessage?: string;
}

export const EventList: React.FC<EventListProps> = ({
  events,
  view,
  isLoading = false,
  onView,
  emptyMessage,
}) => {
  const { t } = useTranslation("events");
  const [searchTerm, setSearchTerm] = useState("");

  const now = useMemo(() => new Date(), []);

  // Filter by view: upcoming = endDate >= now, past = endDate < now
  const viewFilteredEvents = useMemo(
    () =>
      events
        .filter((e) =>
          view === "upcoming"
            ? new Date(e.endDate) >= now
            : new Date(e.endDate) < now,
        )
        .sort((a, b) => {
          const diff =
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
          return view === "upcoming" ? diff : -diff;
        }),
    [events, view, now],
  );

  // Apply search on top of view-filtered events
  const displayedEvents = useMemo(() => {
    if (searchTerm === "") return viewFilteredEvents;
    const lower = searchTerm.toLowerCase();
    return viewFilteredEvents.filter(
      (e) =>
        e.name.toLowerCase().includes(lower) ||
        e.description?.toLowerCase().includes(lower) ||
        e.track.name.toLowerCase().includes(lower),
    );
  }, [viewFilteredEvents, searchTerm]);

  if (isLoading) {
    return (
 <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
 className="bg-white rounded-lg border border-secondary-200 p-6 animate-pulse"
          >
 <div className="h-6 bg-secondary-200 rounded mb-4 w-3/4"></div>
 <div className="h-4 bg-secondary-200 rounded mb-2 w-1/2"></div>
 <div className="h-4 bg-secondary-200 rounded mb-4 w-2/3"></div>
 <div className="h-4 bg-secondary-200 rounded w-1/4"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
 <div className="space-y-6">
      {/* Search */}
      <input
        type="text"
        placeholder={t("searchPlaceholder")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
      />

      {searchTerm && displayedEvents.length !== viewFilteredEvents.length && (
 <div className="text-sm text-secondary-600 -mt-4">
          {t("showing", {
            count: displayedEvents.length,
            total: viewFilteredEvents.length,
          })}
        </div>
      )}

      {/* Event List */}
      {displayedEvents.length === 0 ? (
 <div className="bg-white rounded-lg border border-secondary-200 p-8 text-center">
 <div className="text-secondary-400 mb-4">
            <svg
 className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V7a2 2 0 012-2h4a2 2 0 012 2v0M8 7v8a2 2 0 002 2h4a2 2 0 002-2V7M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2"
              />
            </svg>
          </div>
 <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {t("noEventsFound")}
          </h3>
 <p className="text-secondary-600">
            {emptyMessage || t("noEventsMessage")}
          </p>
        </div>
      ) : (
 <div className="space-y-4">
          {displayedEvents.map((event) => (
            <EventCard key={event.id} event={event} onView={onView} />
          ))}
        </div>
      )}
    </div>
  );
};
